import * as React from "react";

import { cn } from "@/lib/utils";
import { ButtonGroup, ButtonGroupItem } from "@/components/ui/button-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export type SplitButtonVariant = "default" | "outline";
export type SplitButtonSize = "xs" | "sm" | "md";
export type SplitButtonVisualState =
  | "default"
  | "hover-left"
  | "hover-right"
  | "pressed-left"
  | "pressed-right"
  | "disabled";

export interface SplitButtonMenuItem {
  key: string;
  label: React.ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  variant?: "default" | "destructive";
  className?: string;
}

export interface SplitButtonProps {
  leftLabel: React.ReactNode;
  onLeftClick: () => void;
  menuItems: SplitButtonMenuItem[];
  variant?: SplitButtonVariant;
  size?: SplitButtonSize;
  disabled?: boolean;
  leftDisabled?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  menuTriggerAriaLabel?: string;
  className?: string;
  leftButtonClassName?: string;
  rightButtonClassName?: string;
  menuContentClassName?: string;
  menuItemClassName?: string;
  menuContentAlign?: "start" | "center" | "end";
  menuContentSideOffset?: number;
  forceVisualState?: SplitButtonVisualState;
}

type SizeConfig = {
  root: string;
  left: string;
  right: string;
  text: string;
  icon: string;
  rightIcon: string;
  chevron: string;
};

const SIZE_CLASSES: Record<SplitButtonSize, SizeConfig> = {
  xs: {
    root: "h-6 rounded-[8px]",
    left: "gap-2 px-2 py-1",
    right: "shrink-0 w-[31px] justify-end pr-2",
    text: "text-xs leading-4",
    icon: "size-[15px] text-[12px]",
    rightIcon: "size-[15px]",
    chevron: "text-[12px]",
  },
  sm: {
    root: "h-8 rounded-[8px]",
    left: "gap-2 pl-3 pr-2 py-[6px]",
    right: "shrink-0 w-9 justify-end pr-2",
    text: "text-sm leading-5",
    icon: "size-5 text-[16px]",
    rightIcon: "size-5",
    chevron: "text-xs",
  },
  md: {
    root: "h-10 rounded-[8px]",
    left: "gap-2 pl-4 pr-3 py-[10px]",
    right: "shrink-0 w-11 justify-end pr-3",
    text: "text-sm leading-5",
    icon: "size-5 text-[16px]",
    rightIcon: "size-5",
    chevron: "text-[16px]",
  },
};

function getRootVariantClasses(variant: SplitButtonVariant) {
  if (variant === "default") {
    return "bg-blue-500 text-white shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]";
  }

  return "bg-white border border-neutral-100 text-neutral-700 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]";
}

function getLeftVariantClasses(
  variant: SplitButtonVariant,
  allowInteractiveStates: boolean
) {
  if (variant === "default") {
    return allowInteractiveStates
      ? "text-white hover:bg-blue-600 active:bg-blue-700"
      : "text-white";
  }

  return allowInteractiveStates
    ? "text-neutral-700 hover:bg-blue-25 hover:text-blue-500 active:bg-blue-50 active:text-blue-500"
    : "text-neutral-700";
}

function getRightVariantClasses(
  variant: SplitButtonVariant,
  allowInteractiveStates: boolean
) {
  if (variant === "default") {
    return allowInteractiveStates
      ? "text-white border-white/50 hover:bg-blue-600 active:bg-blue-700"
      : "text-white border-white/50";
  }

  return allowInteractiveStates
    ? "text-neutral-700 border-neutral-100 hover:bg-blue-25 active:bg-blue-50"
    : "text-neutral-700 border-neutral-100";
}

function getForcedLeftStateClasses(
  variant: SplitButtonVariant,
  state?: SplitButtonVisualState
) {
  if (!state) return null;

  if (variant === "default") {
    if (state === "hover-left") return "bg-blue-600";
    if (state === "pressed-left") return "bg-blue-700";
    return null;
  }

  if (state === "hover-left") return "bg-blue-25 text-blue-500";
  if (state === "pressed-left") return "bg-blue-50 text-blue-500";
  return null;
}

function getForcedRightStateClasses(
  variant: SplitButtonVariant,
  state?: SplitButtonVisualState
) {
  if (!state) return null;

  if (variant === "default") {
    if (state === "hover-right") return "bg-blue-600";
    if (state === "pressed-right") return "bg-blue-700";
    return null;
  }

  if (state === "hover-right") return "bg-blue-25 text-blue-500";
  if (state === "pressed-right")
    return "border-blue-100 bg-blue-50 text-blue-500";
  return null;
}

export const SplitButton = React.forwardRef<
  HTMLButtonElement,
  SplitButtonProps
>(
  (
    {
      leftLabel,
      onLeftClick,
      menuItems,
      variant = "outline",
      size = "sm",
      disabled = false,
      leftDisabled = false,
      leadingIcon,
      trailingIcon,
      menuTriggerAriaLabel = "More options",
      className,
      leftButtonClassName,
      rightButtonClassName,
      menuContentClassName,
      menuItemClassName,
      menuContentAlign = "end",
      menuContentSideOffset = 4,
      forceVisualState,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const sizeClasses = SIZE_CLASSES[size];

    const visualState = forceVisualState ?? (disabled ? "disabled" : "default");
    const isVisuallyDisabled = visualState === "disabled";
    const isInteractionDisabled = disabled;
    const allowInteractiveVisualStates = !isVisuallyDisabled;
    const showPressedRight =
      !isVisuallyDisabled && (visualState === "pressed-right" || open);

    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <ButtonGroup
          data-slot="split-button"
          className={cn(
            "overflow-hidden",
            sizeClasses.root,
            getRootVariantClasses(variant),
            isVisuallyDisabled && "opacity-40",
            isInteractionDisabled && "cursor-not-allowed",
            className
          )}
        >
          <ButtonGroupItem
            ref={ref}
            data-slot="split-button-left"
            data-skedui
            type="button"
            onClick={onLeftClick}
            disabled={isInteractionDisabled || leftDisabled}
            className={cn(
              "inline-flex h-full items-center justify-center whitespace-nowrap font-inter font-medium transition-colors duration-150",
              "appearance-none rounded-none border-0 bg-transparent m-0",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500",
              "disabled:cursor-not-allowed",
              sizeClasses.left,
              sizeClasses.text,
              getLeftVariantClasses(variant, allowInteractiveVisualStates),
              getForcedLeftStateClasses(variant, forceVisualState),
              leftButtonClassName
            )}
          >
            {leadingIcon ? (
              <span
                aria-hidden="true"
                className={cn(
                  "inline-flex shrink-0 items-center justify-center",
                  sizeClasses.icon
                )}
              >
                {leadingIcon}
              </span>
            ) : null}
            <span>{leftLabel}</span>
            {trailingIcon ? (
              <span
                aria-hidden="true"
                className={cn(
                  "inline-flex shrink-0 items-center justify-center",
                  sizeClasses.icon
                )}
              >
                {trailingIcon}
              </span>
            ) : null}
          </ButtonGroupItem>

          <DropdownMenuTrigger asChild disabled={isInteractionDisabled}>
            <ButtonGroupItem
              data-slot="split-button-right"
              data-skedui
              type="button"
              disabled={isInteractionDisabled}
              aria-label={menuTriggerAriaLabel}
              className={cn(
                "inline-flex h-full items-center justify-center border-l font-inter transition-colors duration-150",
                "appearance-none rounded-none bg-transparent m-0",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500",
                "disabled:cursor-not-allowed",
                sizeClasses.right,
                getRightVariantClasses(variant, allowInteractiveVisualStates),
                showPressedRight &&
                  getForcedRightStateClasses(variant, "pressed-right"),
                !showPressedRight &&
                  getForcedRightStateClasses(variant, forceVisualState),
                rightButtonClassName
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  "inline-flex shrink-0 items-center justify-center",
                  sizeClasses.rightIcon
                )}
              >
                <i
                  aria-hidden="true"
                  className={cn(
                    "fa-regular transition-transform",
                    showPressedRight ? "fa-chevron-up" : "fa-chevron-down",
                    sizeClasses.chevron
                  )}
                />
              </span>
            </ButtonGroupItem>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align={menuContentAlign}
            sideOffset={menuContentSideOffset}
            className={cn(
              variant === "outline" &&
                "min-w-0 border-none bg-transparent p-0 shadow-none",
              menuContentClassName
            )}
          >
            {menuItems.map((item) => (
              <DropdownMenuItem
                key={item.key}
                disabled={item.disabled}
                variant={item.variant}
                onSelect={() => item.onSelect()}
                className={cn(
                  variant === "outline"
                    ? "rounded-lg border border-blue-100 bg-white px-3 py-[6px] font-inter text-sm font-medium text-blue-500 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] hover:bg-blue-25 hover:text-blue-500 focus:bg-blue-25 focus:text-blue-500"
                    : size === "xs"
                      ? "text-xs"
                      : "text-sm",
                  menuItemClassName,
                  item.className
                )}
              >
                {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </ButtonGroup>
      </DropdownMenu>
    );
  }
);

SplitButton.displayName = "SplitButton";
